// The interactive phone-menu guide for one location — the in-app recreation of
// the static "Telefoonmenu" HTML: a sidebar with sections, search and language
// switch, and the section body. Works inside the Teams tab and on the
// standalone /menu page (the host passes dialMode).

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { openInTeams } from '../../api/client';
import { Home, GitBranch, Building2, Users, Clock, ListTree, Info, Printer, Pencil, Search, MapPin, ChevronDown, ExternalLink } from 'lucide-react';
import type { CallFlow } from '../../api/client';
import { GuideContext, parseHash, routeToHash, type DialTarget, type GuideRoute, type GuideSection } from './context';
import { buildGuideModel, nodeName, flowTitle, shortestPath } from './model';
import { useLang } from './i18n';
import type { PresenceInfo } from './lib/callFlowStatus';
import { StartSection } from './StartSection';
import { SimulatorSection } from './SimulatorSection';
import { DepartmentsSection } from './DepartmentsSection';
import { PeopleSection } from './PeopleSection';
import { HoursSection } from './HoursSection';
import { StructureSection } from './StructureSection';
import { AboutSection } from './AboutSection';

export interface PhoneMenuGuideProps {
  flows: CallFlow[];
  flow: CallFlow;
  onSelectFlow: (id: string) => void;
  dirById: Record<string, DialTarget>;
  onDial: (target: DialTarget) => void;
  dialMode?: 'teams' | 'web';
  accentColor: string;
  isAdmin?: boolean;
  onEdit?: (flow: CallFlow) => void;
  presence?: Map<string, PresenceInfo>;
  presenceState?: 'ok' | 'consent' | 'unavailable' | 'loading';
  presenceConsentUrl?: string;
  /** Called with the agent ids on screen so the host can poll presence for them. */
  onVisibleAgents?: (ids: string[]) => void;
  /** When set, the sidebar offers "Open in browser" for this URL (the Teams
   *  tab links to the standalone /menu page). */
  browserUrl?: string;
  /** Extra header actions. */
  headerExtra?: ReactNode;
  /** Hash routing is shared with the page; a host that already owns the hash
   *  can turn it off and keep the route in memory. */
  useHash?: boolean;
}

const NAV: Array<{ section: GuideSection; icon: typeof Home; key: 'start' | 'callflow' | 'departments' | 'people' | 'hours' | 'structure' | 'about' }> = [
  { section: 'start', icon: Home, key: 'start' },
  { section: 'callflow', icon: GitBranch, key: 'callflow' },
  { section: 'departments', icon: Building2, key: 'departments' },
  { section: 'people', icon: Users, key: 'people' },
  { section: 'hours', icon: Clock, key: 'hours' },
  { section: 'structure', icon: ListTree, key: 'structure' },
  { section: 'about', icon: Info, key: 'about' },
];

function useMinuteTick(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function PhoneMenuGuide({
  flows, flow, onSelectFlow, dirById, onDial, dialMode = 'teams', accentColor, isAdmin, onEdit,
  presence, presenceState, presenceConsentUrl, onVisibleAgents, browserUrl, headerExtra, useHash = true,
}: PhoneMenuGuideProps) {
  const [lang, setLang, t] = useLang();
  const now = useMinuteTick();
  const model = useMemo(() => buildGuideModel(flow), [flow.id, flow.updatedAt, flow]); // eslint-disable-line react-hooks/exhaustive-deps
  const [route, setRoute] = useState<GuideRoute>(() => (useHash ? parseHash(window.location.hash) : { section: 'start' }));
  const [callPath, setCallPath] = useState<string[]>([model.rootId]);
  const [search, setSearch] = useState('');
  const [navOpen, setNavOpen] = useState(false);

  // Keep the section in the URL hash so links are shareable and Back works.
  useEffect(() => {
    if (!useHash) return;
    const onHash = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [useHash]);

  const navigate = useCallback((r: GuideRoute) => {
    setRoute(r);
    setNavOpen(false);
    if (useHash) {
      const h = routeToHash(r);
      if (window.location.hash !== h) window.location.hash = h;
    }
    if (r.section === 'callflow') {
      // Walk the shortest route to the requested node; the root otherwise.
      const p = r.id ? shortestPath(model, r.id) : undefined;
      setCallPath(p ? [model.rootId, ...p.steps.map(s => s.toId)] : [model.rootId]);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [model, useHash]);

  // A new location (or a re-synced one) resets the walk.
  useEffect(() => { setCallPath(p => (p.every(id => model.byId.has(id)) ? p : [model.rootId])); }, [model]);

  // Tell the host which agents are on screen (presence polling scope).
  useEffect(() => {
    if (!onVisibleAgents) return;
    if (route.section === 'departments' && route.id) {
      const q = model.byId.get(route.id);
      onVisibleAgents(q && q.type === 'queue' ? (q.agents || []).map(a => a.id.toLowerCase()) : []);
    } else if (route.section === 'callflow') {
      const q = model.byId.get(callPath[callPath.length - 1]);
      onVisibleAgents(q && q.type === 'queue' ? (q.agents || []).map(a => a.id.toLowerCase()) : []);
    } else {
      onVisibleAgents(model.agentIds);
    }
  }, [route, callPath, model, onVisibleAgents]);

  const results = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return [];
    const out: Array<{ kind: 'menu' | 'department' | 'person'; label: string; sub: string; route: GuideRoute }> = [];
    for (const m of model.menus) if (nodeName(m, lang).toLowerCase().includes(s) || m.label.toLowerCase().includes(s)) out.push({ kind: 'menu', label: nodeName(m, lang), sub: m.label, route: { section: 'callflow', id: m.id } });
    for (const q of model.queues) if (nodeName(q, lang).toLowerCase().includes(s) || q.label.toLowerCase().includes(s)) out.push({ kind: 'department', label: nodeName(q, lang), sub: q.label, route: { section: 'departments', id: q.id } });
    for (const id of model.agentIds) {
      const d = dirById[id];
      if (!d) continue;
      if (d.displayName.toLowerCase().includes(s) || (d.email || '').toLowerCase().includes(s)) {
        const qs = model.queuesByAgent.get(id) || [];
        out.push({ kind: 'person', label: d.displayName, sub: qs.map(q => nodeName(q, lang)).join(', '), route: { section: 'departments', id: qs[0]?.id } });
      }
    }
    return out.slice(0, 12);
  }, [search, model, lang, dirById]);

  const ctx = useMemo(() => ({
    flow, model, lang, t, accent: accentColor, dirById, onDial, dialMode, presence, presenceState, presenceConsentUrl, now, route, navigate, isAdmin: !!isAdmin,
  }), [flow, model, lang, t, accentColor, dirById, onDial, dialMode, presence, presenceState, presenceConsentUrl, now, route, navigate, isAdmin]);

  const body = (() => {
    switch (route.section) {
      case 'callflow': return <SimulatorSection path={callPath} setPath={setCallPath} />;
      case 'departments': return <DepartmentsSection selectedId={route.id} />;
      case 'people': return <PeopleSection />;
      case 'hours': return <HoursSection selectedId={route.id} />;
      case 'structure': return <StructureSection />;
      case 'about': return <AboutSection />;
      default: return <StartSection />;
    }
  })();

  const currentNav = NAV.find(n => n.section === route.section) || NAV[0];

  return (
    <GuideContext.Provider value={ctx}>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden grid md:grid-cols-[230px_minmax(0,1fr)] print:block" style={{ fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
        {/* Sidebar */}
        <aside className="border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50/60 print:hidden">
          <div className="p-3 space-y-3">
            {flows.length > 1 && (
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">{t('location')}</label>
                <div className="relative">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                  <select value={flow.id} onChange={e => onSelectFlow(e.target.value)}
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-purple-500">
                    {flows.map(f => <option key={f.id} value={f.id}>{flowTitle(f, lang)}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search')}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
              {search.trim() && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto">
                  {results.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">{t('noResults')}</div>}
                  {results.map((r, i) => (
                    <button key={i} type="button" onClick={() => { navigate(r.route); setSearch(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-start gap-2">
                      <span className="text-[9px] font-bold uppercase w-9 shrink-0 mt-0.5 text-center rounded bg-gray-800 text-white px-1 py-0.5">
                        {r.kind === 'menu' ? t('resultMenu').slice(0, 4) : r.kind === 'department' ? t('resultDepartment').slice(0, 4) : t('resultPerson').slice(0, 4)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-gray-900 truncate">{r.label}</span>
                        <span className="block text-[11px] text-gray-500 truncate">{r.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile: current section as a dropdown; desktop: the list */}
            <button type="button" onClick={() => setNavOpen(o => !o)} className="md:hidden w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded bg-white">
              <span className="inline-flex items-center gap-2"><currentNav.icon className="w-4 h-4 text-gray-500" />{t(currentNav.key)}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            <nav className={`${navOpen ? 'block' : 'hidden'} md:block`}>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 hidden md:block">{t('navigation')}</div>
              <ul className="space-y-0.5">
                {NAV.map(n => {
                  const active = route.section === n.section;
                  const count = n.section === 'departments' ? model.queues.length : n.section === 'people' ? model.agentIds.length : undefined;
                  return (
                    <li key={n.section}>
                      <button type="button" onClick={() => navigate({ section: n.section })}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded text-left ${active ? 'bg-white shadow-sm font-medium' : 'text-gray-700 hover:bg-white/70'}`}
                        style={active ? { color: accentColor, boxShadow: `inset 3px 0 0 ${accentColor}` } : undefined}>
                        <n.icon className="w-4 h-4 shrink-0" />
                        <span className="flex-1 truncate">{t(n.key)}</span>
                        {count !== undefined && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{count}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex items-center gap-1.5 flex-wrap pt-1">
              <div className="inline-flex border border-gray-200 rounded bg-white p-0.5">
                {(['nl', 'en'] as const).map(l => (
                  <button key={l} type="button" onClick={() => setLang(l)}
                    className={`px-2 py-0.5 text-[11px] font-semibold rounded ${lang === l ? 'text-white' : 'text-gray-600'}`}
                    style={lang === l ? { backgroundColor: accentColor } : undefined}>{l.toUpperCase()}</button>
                ))}
              </div>
              <button type="button" onClick={() => window.print()} title={t('print')}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50">
                <Printer className="w-3.5 h-3.5" />{t('print')}
              </button>
              {isAdmin && onEdit && (
                <button type="button" onClick={() => onEdit(flow)} title={t('edit')}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50">
                  <Pencil className="w-3.5 h-3.5" />{t('edit')}
                </button>
              )}
              {browserUrl && (
                <button type="button" onClick={() => openInTeams(browserUrl)} title={t('openInBrowser')}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50">
                  <ExternalLink className="w-3.5 h-3.5" />{t('openInBrowser')}
                </button>
              )}
              {headerExtra}
            </div>

            <div className="border-l-4 rounded-r bg-white px-3 py-2 text-xs text-gray-600" style={{ borderColor: accentColor }}>
              <div className="font-semibold text-gray-800">{t('mainNumber')}</div>
              <div className="tabular-nums">{flow.mainNumbers.join(', ')}</div>
              <div className="mt-0.5">{t('scopeText')}</div>
            </div>
          </div>
        </aside>

        {/* Body */}
        <main className="min-w-0 p-4 md:p-5 bg-gray-50/30 print:p-0">
          <div className="print:block hidden mb-4">
            <h1 className="text-lg font-semibold">{flowTitle(flow, lang)} · {t('footer')}</h1>
            <p className="text-xs text-gray-500">{flow.mainNumbers.join(', ')} · {new Date().toLocaleString()}</p>
          </div>
          {body}
          <div className="mt-6 pt-3 border-t border-gray-200 flex items-center justify-between text-[11px] text-gray-400">
            <span>{flowTitle(flow, lang)} · {t('footer')}</span>
            <span>{t('snapshot')}: {(flow.sync?.lastSyncAt || flow.source?.importedAt || flow.updatedAt || '').slice(0, 10)}</span>
          </div>
        </main>
      </div>
    </GuideContext.Provider>
  );
}
